import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { publicEvaluationCopyAPI } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Download, Mail, ShieldCheck, AlertCircle } from 'lucide-react';
import { SignatureNotice } from '../components/SignatureNotice';

const STEPS = { EMAIL: 'email', OTP: 'otp', READY: 'ready', EXPIRED: 'expired' };

export default function PublicEvaluationCopy() {
  const { token } = useParams();
  const [step, setStep] = useState(STEPS.EMAIL);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [scopedToken, setScopedToken] = useState('');
  const [meta, setMeta] = useState(null);
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => {
    publicEvaluationCopyAPI.meta(token)
      .then((r) => setMeta(r.data))
      .catch((e) => {
        if (e.response?.status === 410) { setStep(STEPS.EXPIRED); setErrMsg(e.response?.data?.detail || ''); }
      });
  }, [token]);

  const handleRequestOtp = async (e) => {
    e?.preventDefault();
    if (!email) { toast.error('Enter your email to continue'); return; }
    setBusy(true); setErrMsg('');
    try {
      await publicEvaluationCopyAPI.requestOtp(token, email.trim());
      toast.success('A 6-digit passcode was emailed to you.');
      setStep(STEPS.OTP);
    } catch (e2) {
      const detail = e2.response?.data?.detail || 'Unable to send passcode';
      if (e2.response?.status === 410) { setStep(STEPS.EXPIRED); setErrMsg(detail); }
      else toast.error(detail);
    } finally { setBusy(false); }
  };

  const handleVerifyOtp = async (e) => {
    e?.preventDefault();
    if (!otp || otp.length < 4) { toast.error('Enter the 6-digit passcode from your email'); return; }
    setBusy(true);
    try {
      const res = await publicEvaluationCopyAPI.verifyOtp(token, email.trim(), otp.trim());
      setScopedToken(res.data.access_token);
      setStep(STEPS.READY);
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Verification failed');
    } finally { setBusy(false); }
  };

  const download = async () => {
    setBusy(true);
    try {
      const res = await fetch(publicEvaluationCopyAPI.pdfUrl(token), {
        headers: { Authorization: `Bearer ${scopedToken}` },
      });
      if (!res.ok) throw new Error('download failed');
      const blob = await res.blob();
      // Save with the server-provided filename so the downloaded file reflects
      // the signed phase (e.g. "6-Month-Review-Copy - Jane-Doe.pdf").
      const cd = res.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="?([^"]+)"?/i);
      const label = meta?.display_name || (meta?.evaluation_type ? `${meta.evaluation_type} Evaluation` : 'Evaluation');
      const who = (meta?.employee_name || '').trim().replace(/\s+/g, '-');
      const fallback = `${label} Copy${who ? ` - ${who}` : ''}.pdf`.replace(/\s+/g, '-').replace(/\//g, '-');
      const filename = (m && m[1]) || fallback;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download your copy. Please request a new passcode.');
    } finally { setBusy(false); }
  };

  const evName = meta?.display_name || (meta?.evaluation_type ? `${meta.evaluation_type} Evaluation` : 'Evaluation');

  return (
    <div className="theme-pin-light min-h-screen bg-slate-50 flex flex-col items-center px-4 py-10" data-testid="public-eval-copy-page">
      <div className="w-full max-w-2xl space-y-6">
        <div className="flex items-center gap-3">
          <img src="/tricorps360-horizontal.png" alt="TriCorps360" className="h-10 w-auto" />
          <div className="border-l border-slate-200 pl-3">
            <h1 className="text-xl font-semibold text-slate-900">{evName} Copy</h1>
            <p className="text-sm text-slate-500">Secure employee copy portal</p>
          </div>
        </div>

        {step === STEPS.EMAIL && (
          <Card data-testid="step-email">
            <CardHeader>
              <CardTitle>Verify your identity</CardTitle>
              <CardDescription>Enter the email address where you received this link. We'll send you a 6-digit passcode to view your completed evaluation.</CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-4" onSubmit={handleRequestOtp}>
                <div className="space-y-2">
                  <Label htmlFor="email">Email address</Label>
                  <Input id="email" type="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" data-testid="email-input" />
                </div>
                <Button type="submit" disabled={busy} className="w-full" data-testid="request-otp-btn">
                  <Mail className="w-4 h-4 mr-2" />
                  {busy ? 'Sending...' : 'Email me a passcode'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {step === STEPS.OTP && (
          <Card data-testid="step-otp">
            <CardHeader>
              <CardTitle>Enter your passcode</CardTitle>
              <CardDescription>We just emailed a 6-digit code to <b>{email}</b>. It expires in 10 minutes.</CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-4" onSubmit={handleVerifyOtp}>
                <div className="space-y-2">
                  <Label htmlFor="otp">6-digit passcode</Label>
                  <Input id="otp" inputMode="numeric" pattern="[0-9]*" autoFocus maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} placeholder="123456" className="text-center text-2xl tracking-[0.4em] font-mono" data-testid="otp-input" />
                </div>
                <div className="flex flex-col gap-2">
                  <Button type="submit" disabled={busy} className="w-full" data-testid="verify-otp-btn">
                    <ShieldCheck className="w-4 h-4 mr-2" />
                    {busy ? 'Verifying...' : 'Verify & Continue'}
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setStep(STEPS.EMAIL)} className="w-full" data-testid="otp-back-btn">
                    Use a different email
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {step === STEPS.READY && (
          <Card data-testid="step-ready">
            <CardHeader>
              <CardTitle>Your evaluation copy</CardTitle>
              <CardDescription>
                {meta ? <>For <b>{meta.employee_name}</b>{meta.period_from && <> — {meta.period_from} to {meta.period_to}</>}.</> : 'Your completed evaluation is ready.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-600">You're verified. Open or download a copy of your completed evaluation below.</p>
              <Button onClick={download} disabled={busy} className="w-full" data-testid="copy-download-btn">
                <Download className="w-4 h-4 mr-2" />
                {busy ? 'Opening...' : 'View / download my copy (PDF)'}
              </Button>
              <SignatureNotice hrEmail={meta?.hr_email} />
            </CardContent>
          </Card>
        )}

        {step === STEPS.EXPIRED && (
          <Card data-testid="step-expired">
            <CardContent className="py-10 text-center space-y-3">
              <AlertCircle className="w-12 h-12 text-amber-600 mx-auto" />
              <h2 className="text-xl font-semibold text-slate-900">This link is no longer valid</h2>
              <p className="text-slate-600">{errMsg || 'This copy link has expired.'}</p>
              <p className="text-slate-500 text-sm">Please contact HR to request a copy of your evaluation.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
